import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import EntityTable from '../components/EntityTable';
import EntityDetailPanel from '../components/EntityDetailPanel';
import Toolbar, { Pager } from '../components/Toolbar';
import RelLink from '../components/RelLink';
import { Sets } from '../api/admin';

const SetsTab = ({ filters, goTo }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(Number(filters.page) || 1);
    const [search, setSearch] = useState(filters.setName || '');
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        setSearch(filters.setName || '');
        setPage(Number(filters.page) || 1);
    }, [filters.setName, filters.page]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await Sets.list({ page });
            setRows(Array.isArray(result) ? result : (result.sets || []));
        } catch (e) {
            setError(e.message);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => { load(); }, [load]);

    const filtered = search
        ? rows.filter((r) =>
            (r.name || '').toLowerCase().includes(search.toLowerCase())
            || (r.series || '').toLowerCase().includes(search.toLowerCase()))
        : rows;

    const columns = [
        {
            key: 'logo',
            header: '',
            width: '64px',
            render: (r) =>
                r.logo_image ? <img src={r.logo_image} alt="" className="set-logo" /> : '—',
        },
        { key: 'name', header: 'Name' },
        { key: 'series', header: 'Series' },
        { key: 'printed_total', header: 'Printed' },
        { key: 'total', header: 'Total' },
        {
            key: 'release_date',
            header: 'Released',
            render: (r) => (r.release_date ? new Date(r.release_date).toLocaleDateString() : '—'),
        },
        {
            key: 'view',
            header: 'View',
            render: (r) =>
                r.name ? (
                    <Link
                        to={`/sets/${encodeURIComponent(r.name)}`}
                        className="external-link"
                        onClick={(e) => e.stopPropagation()}
                    >
                        Public →
                    </Link>
                ) : '—',
        },
    ];

    const sectionsFor = (r) => [
        {
            title: 'Set',
            rows: [
                { label: 'ID', value: r.id },
                { label: 'Name', value: r.name },
                { label: 'Series', value: r.series },
                { label: 'Printed Total', value: r.printed_total },
                { label: 'Total', value: r.total },
                { label: 'Release', value: r.release_date ? new Date(r.release_date).toLocaleDateString() : '—' },
            ],
        },
        {
            title: 'Related',
            rows: [
                {
                    label: 'Cards in set',
                    render: () => (
                        <RelLink tab="cards" filters={{ setName: r.name }} goTo={goTo}>
                            View cards →
                        </RelLink>
                    ),
                },
                {
                    label: 'Public page',
                    render: () => (
                        <Link to={`/sets/${encodeURIComponent(r.name)}`} className="external-link">
                            Open set page →
                        </Link>
                    ),
                },
            ],
        },
    ];

    return (
        <div className="admin-tab-panel">
            <Toolbar>
                <input
                    placeholder="Filter sets…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn-ghost" onClick={() => goTo({ tab: 'sets' })}>Clear</button>
            </Toolbar>

            {loading && <p className="loading">Loading…</p>}
            {error && <p className="error-message">Error: {error}</p>}

            {!loading && !error && (
                <EntityTable
                    columns={columns}
                    rows={filtered}
                    onRowClick={(r) => setSelected(r)}
                    emptyMessage="No sets match."
                />
            )}

            <Pager
                page={page}
                totalPages={rows.length === 0 ? page : page + (rows.length === 50 ? 1 : 0)}
                onChange={(p) => goTo({ tab: 'sets', setName: search || undefined, page: p })}
            />

            <EntityDetailPanel
                open={!!selected}
                title={selected ? `Set: ${selected.name}` : ''}
                onClose={() => setSelected(null)}
                sections={selected ? sectionsFor(selected) : []}
            />
        </div>
    );
};

export default SetsTab;
